import { Link } from "wouter";
import { BookOpen, Compass, ListChecks, Library, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeading, Callout, Diagram, NAV_TREE, pathFor } from "../shared";

export default function OverviewSection() {
  return (
    <div data-testid="guide-overview">
      <PageHeading
        icon={BookOpen}
        title="Welcome to the User Guide"
        description="This guide explains the whole system in plain language — what each screen is for, and exactly where to click and what to type. You don't need any technical knowledge to follow it."
      />

      <Callout variant="info" title="How to use this guide">
        The menu on the left is grouped into sections. Click any group to open it, then click a topic to read it. Most topics include a <span className="text-white">labelled picture</span> of the screen with numbered steps — the number on the picture matches the number in the instructions, so you always know where to look.
      </Callout>

      <Diagram
        title="The guide menu (left side of this page)"
        frameLabel="User Guide"
        legend={[
          <>Click a group heading (like "How-To: Filling Forms") to open or close it.</>,
          <>Click a topic to read it. The topic you're reading is highlighted.</>,
          <>Use <span className="text-white">Previous</span> / <span className="text-white">Next</span> at the bottom of each topic to move through the guide in order.</>,
        ]}
      >
        <div className="max-w-xs space-y-1">
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-white/60 bg-white/[0.03]">
            <Compass className="w-3.5 h-3.5" /> <span className="flex-1">Get Started</span>
            <span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">1</span>
          </div>
          <div className="pl-4 space-y-0.5">
            <div className="px-2 py-1.5 rounded-md text-xs bg-cyan-500/15 text-cyan-200 font-medium flex items-center justify-between">
              Overview
              <span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">2</span>
            </div>
            <div className="px-2 py-1.5 rounded-md text-xs text-white/55">Quick Start by Role</div>
            <div className="px-2 py-1.5 rounded-md text-xs text-white/55">Glossary</div>
          </div>
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-white/60">
            <ListChecks className="w-3.5 h-3.5" /> <span className="flex-1">How-To: Filling Forms</span>
          </div>
        </div>
      </Diagram>

      <Card className="bg-amber-500/5 border-amber-500/20 mb-6">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-white font-medium mb-1">Recently added — better people &amp; team reports</p>
              <p className="text-xs text-white/65 leading-relaxed">
                The <Link href={pathFor("pages/hr-dossier")} className="text-cyan-300 hover:underline">HR Dossier</Link> now has an
                <span className="text-white"> Employee Dossier</span> (one person's full record, plus a live "what they're holding today" snapshot)
                and a <span className="text-white">Department Dossier</span> (a whole team's scorecard on one screen).
                <Link href={pathFor("whats-new")} className="text-cyan-300 hover:underline ml-1" data-testid="link-whats-new-overview">See what's new →</Link>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="text-sm text-white/50 mb-3">Or jump straight to a section:</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {NAV_TREE.map((group) => {
          const Icon = group.icon;
          return (
            <Card key={group.id} className="bg-white/[0.03] border-white/10 hover:border-cyan-500/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4 text-cyan-400" />
                  <p className="text-sm font-semibold text-white">{group.label}</p>
                </div>
                <div className="flex flex-col gap-1">
                  {group.items.slice(0, 5).map((item) => (
                    <Link
                      key={item.slug}
                      href={pathFor(item.slug)}
                      className="text-xs text-white/55 hover:text-cyan-300 transition-colors py-0.5"
                      data-testid={`link-overview-${item.slug || "overview"}`}
                    >
                      {item.label}
                    </Link>
                  ))}
                  {group.items.length > 5 && (
                    <p className="text-xs text-white/30 mt-1">+{group.items.length - 5} more</p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Link href={pathFor("quick-start")} className="block" data-testid="link-quick-start-card">
          <Card className="bg-cyan-500/5 border-cyan-500/20 hover:bg-cyan-500/10 transition-colors h-full">
            <CardContent className="p-4">
              <Compass className="w-5 h-5 text-cyan-300 mb-2" />
              <p className="text-sm font-semibold text-white mb-1">First time here?</p>
              <p className="text-xs text-white/55">Start with Quick Start by Role — a short walkthrough written for your job, whether you're a member, an admin, an MD reviewer, or PMO.</p>
            </CardContent>
          </Card>
        </Link>
        <Link href={pathFor("forms/register")} className="block" data-testid="link-register-form-card">
          <Card className="bg-emerald-500/5 border-emerald-500/20 hover:bg-emerald-500/10 transition-colors h-full">
            <CardContent className="p-4">
              <ListChecks className="w-5 h-5 text-emerald-300 mb-2" />
              <p className="text-sm font-semibold text-white mb-1">Need to fill in a form?</p>
              <p className="text-xs text-white/55">Every form has a step-by-step walkthrough with a picture — what each box means, what's required, and what happens after you click Save.</p>
            </CardContent>
          </Card>
        </Link>
        <Link href={pathFor("glossary")} className="block" data-testid="link-glossary-card">
          <Card className="bg-purple-500/5 border-purple-500/20 hover:bg-purple-500/10 transition-colors h-full">
            <CardContent className="p-4">
              <Library className="w-5 h-5 text-purple-300 mb-2" />
              <p className="text-sm font-semibold text-white mb-1">Confused by a word?</p>
              <p className="text-xs text-white/55">The glossary explains SDF, MD, PMO, ExCom, EDGE, "lane", "sign-off", "dispatch" and every other term in plain English.</p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}
